import React, { PureComponent } from 'react';
import { sanitize } from 'dompurify';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import GlobalWrapper from '../GlobalStyle';
import device from '../../constants/device';
import PropertyIconList from '../shared/components/property-icon-list';
import ThemeProvider from '../shared/theme/containers/ThemeProvider';

const PopupImage = styled.div`
    flex: 0 0 120px;
    min-height: 120px;
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;
    max-width: 32%;

    .ie & {
        width: 120px;
    }
`;

const PopupContent = styled.div`
    overflow: hidden;
    font-size: ${props => props.theme.font.size.zeta};
    font-family: ${props => props.theme.font.face.baseFace};
    padding: 20px;
    .ie & {
        width: 255px;
    }
    @media ${device.mobile} {
        padding: 10px;
    }
`;

const Description = styled.div`
    p {
        line-height: 1.43;
        margin-top: 0;
        font-size: ${props => props.theme.font.size.zeta};
    }
    &.line-clamp {
        max-height: ${1.43 * 2}em;
        overflow: hidden;
        margin-bottom: ${props => props.theme.metrics.spacing.half};

        p {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
        }
    }
`;

export const PopupTitle = styled.h5`
    overflow: hidden;
    font-weight: 600;
    text-overflow: ellipsis;
    font-size: ${props => props.theme.font.copy.default};
    color: ${props => props.theme.colors.brown};

    ${props =>
        props.small &&
        `text-align: center;
        width: 100%;`}

    a {
        text-decoration: none;
        color: ${props => props.theme.colors.brown};
    }
`;

const PopupSubtitle = styled.div`
    line-height: 1.43;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin-bottom: ${props => props.theme.metrics.spacing.half};
`;

export default class PopupPoiInfo extends PureComponent {
    render() {
        const pattern = /^((http|https):\/\/)/;
        const { info } = this.props;
        let imageUrl = info.cover && info.cover.src ? info.cover.src : '';

        // Added a check if the cover image url starts with http(s)
        if (imageUrl !== '' && !pattern.test(imageUrl)) {
            imageUrl = `https://www.efteling.com${imageUrl}`;
        }

        const isFairytale = info.category === 'fairytale';
        const isDomain = info.category === 'domain';
        const showDescription = isDomain || isFairytale;

        return (
            <GlobalWrapper>
                {imageUrl && <PopupImage style={{ backgroundImage: `url(${imageUrl})` }} />}
                <PopupContent>
                    <PopupTitle>{info.name}</PopupTitle>
                    {showDescription ? (
                        /* eslint-disable-next-line react/no-danger */
                        <Description
                            className={isFairytale && 'line-clamp'}
                            dangerouslySetInnerHTML={{ __html: sanitize(info.description) }}
                        />
                    ) : (
                        <PopupSubtitle>{info.short_description}</PopupSubtitle>
                    )}

                    {(info.category === 'attraction' || info.category === 'show') && (
                        <ThemeProvider>
                            <PropertyIconList
                                style={{ marginBottom: 10, flexDirection: 'row' }}
                                horizontal
                                limit={5}
                                size={4}
                                features={info.accessibility_features}
                            />
                        </ThemeProvider>
                    )}

                    {info.more_info_web_link && (
                        <a href={info.more_info_web_link} target="_self" rel="noopener noreferrer">
                            {this.props.urlText}
                        </a>
                    )}
                </PopupContent>
            </GlobalWrapper>
        );
    }
}

PopupPoiInfo.propTypes = {
    info: PropTypes.object.isRequired,
    urlText: PropTypes.string.isRequired,
};
